import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RadioButtonGroup,
  Textarea,
} from '@roger/ui-components';
import { getEpisodeTakeOverPlan, takeOverEpisode } from '../../services/api';
import type {
  EpisodeCreateResponse,
  EpisodeTakeoverConfirm,
  EpisodeTakeoverNoa,
  EpisodeTakeoverPlan,
  EpisodeTakeoverPlanPeriod,
  TakeoverCustody,
  TakeoverNoaChoice,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  formatDateForDisplay,
  formatDateToISO,
  getDayAfterDateStr,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { recertCarryAppliedCopy } from '../imported/importReportCopy';
import {
  CHECKLIST_HEADING,
  CHECKLIST_REMAINING_VISITS,
  CHECKLIST_SYNC_REVIEW,
  CHECKLIST_SYNC_REVIEW_FROZEN,
  CHARTS_FROM_EARLIER_LINE,
  CHARTS_FROM_LABEL,
  NOA_ACTION_LINE,
  NOA_EXTERNAL_FILED,
  NOA_NOT_NEEDED,
  NOA_ROGER_SENT,
  NOA_WARNING_LINE,
  FROZEN_LINE,
  OVERRIDE_CONTINUE_LABEL,
  OVERRIDE_INTRO_LINE,
  OVERRIDE_REASON_LABEL,
  OVERRIDE_CLEARED_LINE,
  PLAN_CHANGED_LINE,
  PLAN_RELOAD_FAILED_LINE,
  REFRESH_RUNNING_LINE,
  HISTORY_ARRIVING_LINE,
  HISTORY_MISSING_OVERRIDE_LINE,
  TAKE_OVER_TITLE,
  chartsFromResetLine,
  checklistDoNotSend,
  confirmLine,
  editedRowsLine,
  frozenReasonLine,
  externalKeepsSentence,
  noaOverdueLine,
  noaPayerConfirmLabel,
  noaRogerSends,
  noaSentence,
  refreshWaivedLine,
  rogerBillsFromSentence,
  rogerChartsFromSentence,
  unfinishedVisitLine,
  untilChartsFromLine,
  EXTERNAL_CLAIM_LINES_TITLE,
  EXTERNAL_CLAIM_READING_LINE,
} from './takeOverCopy';
import { custodyForChartsFrom } from './takeoverCustody';

type Step = 'blocked' | 'plan' | 'confirm';
type BlockedPhase = 'loading' | 'unreadable' | 'historyArriving';

type Props = {
  open: boolean;
  onClose: () => void;
  /** The imported period to take over (the plan's episode). */
  episodeId: string;
  onTakenOver: (episode: EpisodeCreateResponse) => void;
};

/** A census day that has come is locked: the confirm accepts only it. */
function plannedDayLocked(plan: EpisodeTakeoverPlan | null): boolean {
  return Boolean(
    plan?.roger_charts_from_planned &&
    plan.roger_charts_from_planned <= plan.today
  );
}

function displayDate(iso: string | null | undefined): string {
  return iso ? formatDateForDisplay(iso) : '';
}

function periodLabel(period: EpisodeTakeoverPlanPeriod): string {
  return `claim ${period.number} (${displayDate(period.start_date)} – ${displayDate(period.end_date)})`;
}

/**
 * Who files the notice, as the plan allows it. "Roger sends" is a choice
 * only for a notice still in Roger's queue with no External EMR mark; any
 * other notice is shown as it stands.
 */
function noaPhrase(noa: EpisodeTakeoverNoa, choice: TakeoverNoaChoice): string {
  // Not a choice and not the External EMR's: the payer needs none, or Roger
  // already sent it.
  if (!noa.choice_available && !noa.accepted_externally) {
    return noa.status === 'not_applicable' ? NOA_NOT_NEEDED : NOA_ROGER_SENT;
  }
  return choice === 'roger_sends'
    ? noaRogerSends(noa.due_date ? displayDate(noa.due_date) : null)
    : NOA_EXTERNAL_FILED;
}

/**
 * Take Over in two steps: the plan, then confirm. Custody follows the
 * cut-off day and is not editable. A frozen agency, or a chart still
 * arriving, stops before the plan.
 */
export default function TakeOverDialog({
  open,
  onClose,
  episodeId,
  onTakenOver,
}: Props) {
  const { success: showSuccess, error: showError } = useToast();
  const [step, setStep] = useState<Step>('blocked');
  const [phase, setPhase] = useState<BlockedPhase>('loading');
  const [plan, setPlan] = useState<EpisodeTakeoverPlan | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [usingOverride, setUsingOverride] = useState(false);
  const [noaChoice, setNoaChoice] = useState<TakeoverNoaChoice | null>(null);
  const [noaPayerConfirmed, setNoaPayerConfirmed] = useState(false);
  const [chartsFrom, setChartsFrom] = useState<string>('');
  // A day the person picked survives a plan reload while it still fits the
  // new plan; otherwise it falls back to the proposal and says so.
  const pickedChartsFrom = useRef<string | null>(null);
  const [chartsFromReset, setChartsFromReset] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A confirm was refused 409 and the fresh plan has not loaded yet: the
  // digest on screen is stale, so Take over stays off.
  const [planStale, setPlanStale] = useState(false);
  // A plan reload after a confirm 409 is in flight: Try again waits for it,
  // so two clicks never start two plan reads.
  const [reloading, setReloading] = useState(false);
  const reloadingRef = useRef(false);

  // Each open (and each close) starts a new generation, so a response from
  // an earlier one never lands on the current dialog.
  const generation = useRef(0);
  const usingOverrideRef = useRef(false);

  const clearOverride = () => {
    usingOverrideRef.current = false;
    setUsingOverride(false);
  };

  const showPlan = useCallback((next: EpisodeTakeoverPlan) => {
    setPlan(next);
    setNoaChoice(next.noa?.default_choice ?? null);
    setNoaPayerConfirmed(false);
    const picked = pickedChartsFrom.current;
    // A locked census day replaces any earlier pick.
    const keep =
      picked !== null &&
      !plannedDayLocked(next) &&
      picked >= next.roger_charts_from_earliest &&
      picked <= next.roger_charts_from_latest;
    if (picked !== null && !keep) pickedChartsFrom.current = null;
    // The day planned on the census wins over the proposal.
    const prefilled =
      next.roger_charts_from_planned ?? next.roger_charts_from_proposed;
    setChartsFromReset(picked !== null && !keep ? prefilled : null);
    setChartsFrom(keep ? picked : prefilled);
    setStep('plan');
  }, []);

  // After the last day in the External EMR, a read that fails is expected:
  // go on to the plan with the records Roger holds (Daniel, 2026-10-15).
  const waiveRefresh = useCallback(
    (next: EpisodeTakeoverPlan): boolean => {
      if (!next.refresh.waivable || next.import_live) return false;
      setNotice(refreshWaivedLine(displayDate(next.external_emr_last_day)));
      showPlan(next);
      return true;
    },
    [showPlan]
  );

  const loadPlan = useCallback(
    async (gen: number): Promise<EpisodeTakeoverPlan | null> => {
      const result = await getEpisodeTakeOverPlan({
        path: { episode_id: episodeId },
        throwOnError: false,
      });
      if (gen !== generation.current) return null;
      if (!result.data) {
        console.error('Failed to load the Take Over plan:', result.error);
        setLoadError(
          getBusinessErrorMessage(result) ??
            'Unable to load the Take Over plan. Please try again.'
        );
        return null;
      }
      return result.data;
    },
    [episodeId]
  );

  // Decide where the dialog stands from a freshly read plan.
  const route = useCallback(
    (next: EpisodeTakeoverPlan): void => {
      setPlan(next);
      setPlanStale(false);
      // Earlier episodes are still arriving: Take Over waits, as the server
      // refuses it until they land. At a frozen agency the override cancels
      // a backfill still queued, so it is offered then. It waits for one
      // that is running: the plan counts that as a live import.
      if (next.history_arriving) {
        if (!next.can_override_access_lost || next.import_live) {
          clearOverride();
          setNotice(null);
          setStep('blocked');
          setPhase('historyArriving');
          return;
        }
        // Even a fresh refresh goes through the override here: without it
        // the server refuses the confirm.
        if (usingOverrideRef.current) {
          showPlan(next);
          return;
        }
        setStep('blocked');
        setPhase('unreadable');
        return;
      }
      const { refresh } = next;
      // The override is only for an External EMR Roger cannot read. Once it
      // can be read again, or a refresh is running, the override no longer
      // applies: the server refuses it, so drop it and refresh instead.
      if (
        usingOverrideRef.current &&
        (refresh.can_refresh || next.import_live)
      ) {
        clearOverride();
      }
      if (usingOverrideRef.current) {
        showPlan(next);
        return;
      }
      // Confirm sets the cut-off from the current chart; opening
      // Take Over does not queue or wait on an External EMR refresh.
      if (refresh.can_refresh || next.import_live) {
        showPlan(next);
        return;
      }
      if (waiveRefresh(next)) return;
      setStep('blocked');
      setPhase('unreadable');
    },
    [showPlan, waiveRefresh]
  );

  const start = useCallback(async () => {
    generation.current += 1;
    const gen = generation.current;
    setLoadError(null);
    setSubmitError(null);
    setPlanStale(false);
    // A confirm from an earlier open may have left these set.
    setSubmitting(false);
    reloadingRef.current = false;
    setReloading(false);
    setPhase('loading');
    setStep('blocked');
    const next = await loadPlan(gen);
    if (next) route(next);
  }, [loadPlan, route]);

  useEffect(() => {
    if (!open) {
      generation.current += 1;
      return;
    }
    usingOverrideRef.current = false;
    setUsingOverride(false);
    setOverrideReason('');
    setNotice(null);
    setPlan(null);
    pickedChartsFrom.current = null;
    setChartsFromReset(null);
    void start();
    return () => {
      // A plan read already in flight must not land after unmount.
      generation.current += 1;
    };
  }, [open, episodeId, start]);

  // After a confirm 409: read the plan again and show it from the top.
  const reloadPlan = async (gen: number) => {
    setLoadError(null);
    const next = await loadPlan(gen);
    if (!next) {
      // Stay where we are; the stale digest must not be sent again.
      if (gen === generation.current) setLoadError(PLAN_RELOAD_FAILED_LINE);
      return;
    }
    const overrideStillApplies =
      usingOverrideRef.current &&
      !next.refresh.can_refresh &&
      !next.import_live;
    let line = PLAN_CHANGED_LINE;
    if (usingOverrideRef.current && !overrideStillApplies) {
      line = next.import_live ? REFRESH_RUNNING_LINE : OVERRIDE_CLEARED_LINE;
    } else if (!overrideStillApplies && next.import_live) {
      line = REFRESH_RUNNING_LINE;
    }
    setNotice(line);
    route(next);
  };

  const reloadAfterConflict = async (gen: number) => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    setReloading(true);
    try {
      await reloadPlan(gen);
    } finally {
      if (gen === generation.current) {
        reloadingRef.current = false;
        setReloading(false);
      }
    }
  };

  const retryRefresh = () => {
    setNotice(null);
    void start();
  };

  const continueWithoutRefresh = () => {
    if (!plan || !overrideReason.trim()) return;
    usingOverrideRef.current = true;
    setUsingOverride(true);
    showPlan(plan);
  };

  // ── The plan for the chosen cut-off day ──
  const periods = plan?.periods ?? [];
  const custodyByStart =
    plan && chartsFrom
      ? custodyForChartsFrom(periods, chartsFrom, plan.today)
      : new Map<string, TakeoverCustody>();
  const custodyOf = (period: EpisodeTakeoverPlanPeriod): TakeoverCustody =>
    custodyByStart.get(period.start_date) ?? period.default_custody;
  const kept = periods.filter(p => custodyOf(p) === 'external_emr');
  const rogerPeriods = periods.filter(p => custodyOf(p) === 'roger');
  // Roger bills one unbroken run after the last period the External EMR keeps.
  let rogerBillsFrom: string | null = null;
  if (plan && rogerPeriods.length > 0) {
    rogerBillsFrom =
      kept.length === 0
        ? plan.start_date
        : getDayAfterDateStr(kept[kept.length - 1].end_date);
  }

  const chartsFromLocked = plannedDayLocked(plan);
  // The day Roger charts from, inside the period and never after the day
  // after the last day in the External EMR.
  const chartsFromValid = Boolean(
    plan &&
    chartsFrom &&
    (chartsFromLocked
      ? chartsFrom === plan.roger_charts_from_planned
      : chartsFrom >= plan.roger_charts_from_earliest &&
        chartsFrom <= plan.roger_charts_from_latest)
  );
  const chartsFromEarlier = Boolean(plan && chartsFrom < plan.today);
  const chartsFromLater = Boolean(plan && chartsFrom > plan.today);

  const noa = plan?.noa ?? null;
  // "Roger sends" is offered only for a notice still in Roger's queue. A
  // notice marked filed from the External EMR, or listed on its Claims tab,
  // is never reopened: Roger will not send a second one.
  const noaFiledExternally = Boolean(
    noa && (noa.accepted_externally || noa.evidence_shown)
  );
  const noaChoosable = Boolean(noa?.choice_available && !noaFiledExternally);
  const effectiveNoa: TakeoverNoaChoice | null = noa
    ? noaFiledExternally
      ? 'kinnser_filed'
      : noaChoosable
        ? (noaChoice ?? noa.default_choice)
        : noa.default_choice
    : null;
  const noaDue = noa?.due_date ? displayDate(noa.due_date) : null;
  // A notice filed in the External EMR for another payer keeps that payer
  // unless the office ticks the box.
  const noaPayerMismatch =
    noaFiledExternally && noa?.recorded_payer_name && noa.current_payer_name
      ? { recorded: noa.recorded_payer_name, current: noa.current_payer_name }
      : null;
  // Only when Roger is about to send the notice: never for one Roger already
  // sent, or one the payer does not need.
  const showNoaWarning =
    noaChoosable &&
    effectiveNoa === 'roger_sends' &&
    periods.length > 0 &&
    custodyOf(periods[0]) === 'external_emr';

  const handleConfirm = async () => {
    if (!plan || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    const body: EpisodeTakeoverConfirm = {
      plan_digest: plan.digest,
      roger_charts_from: chartsFrom,
    };
    // Always name the notice choice when the server allows one: its default
    // can differ from what this dialog shows (a notice in Roger's queue that
    // the External EMR's Claims tab lists is shown, and sent, as filed).
    if (noa?.choice_available && effectiveNoa) body.noa_choice = effectiveNoa;
    if (noaPayerMismatch && noaPayerConfirmed) body.noa_payer_confirmed = true;
    if (usingOverride) body.access_lost_reason = overrideReason.trim();
    const gen = generation.current;
    try {
      const result = await takeOverEpisode({
        path: { episode_id: episodeId },
        body,
        throwOnError: false,
      });
      if (result.data) {
        // The server committed the move. The page must learn of it even when
        // this dialog closed or reopened while the confirm was in flight.
        const taken = result.data.episode;
        onTakenOver(taken);
        if (gen !== generation.current) return;
        onClose();
        const carry = taken.recert_diagnosis_carry;
        if (carry?.applied) {
          showSuccess(recertCarryAppliedCopy(carry.codes?.length ?? 0));
        } else if (carry?.reason) {
          showError(carry.reason);
        }
        return;
      }
      if (gen !== generation.current) return;
      console.error('Take Over was refused:', result.error);
      if (getHttpStatus(result) === 409) {
        // The plan went stale, the refresh aged out, a refresh started, or
        // the External EMR can be read again after all.
        setPlanStale(true);
        await reloadAfterConflict(gen);
        return;
      }
      setSubmitError(
        getBusinessErrorMessage(result) ?? 'Failed to take over the chart.'
      );
    } finally {
      if (gen === generation.current) setSubmitting(false);
    }
  };

  // ── Rendering ──
  const renderRefreshStep = () => {
    if (loadError) {
      return (
        <p
          className='text-sm text-destructive'
          data-testid='takeover-load-error'
        >
          {loadError}
        </p>
      );
    }
    if (phase === 'loading') {
      return <p className='text-sm text-muted-foreground'>Loading the plan…</p>;
    }
    if (phase === 'historyArriving') {
      return (
        <p className='text-sm' role='status' data-testid='takeover-history'>
          {HISTORY_ARRIVING_LINE}
        </p>
      );
    }
    // unreadable: a frozen agency, where Roger no longer syncs.
    return (
      <div className='space-y-3' data-testid='takeover-unreadable'>
        <p className='text-sm'>{FROZEN_LINE}</p>
        {plan?.can_override_access_lost && (
          <div className='space-y-2' data-testid='takeover-override'>
            <p className='text-sm'>{OVERRIDE_INTRO_LINE}</p>
            {plan.history_arriving && (
              <Alert data-testid='takeover-override-history'>
                <AlertDescription>
                  {HISTORY_MISSING_OVERRIDE_LINE}
                </AlertDescription>
              </Alert>
            )}
            <Label htmlFor='takeover-override-reason'>
              {OVERRIDE_REASON_LABEL}
            </Label>
            <Textarea
              id='takeover-override-reason'
              textareaSize='sm'
              value={overrideReason}
              maxLength={1000}
              onChange={event => setOverrideReason(event.target.value)}
            />
          </div>
        )}
      </div>
    );
  };

  const renderNoa = () => {
    if (!noa || !effectiveNoa) return null;
    return (
      <section
        className='space-y-2 rounded-md border p-3'
        aria-labelledby='takeover-noa-heading'
        data-testid='takeover-noa'
      >
        <h3 id='takeover-noa-heading' className='text-sm font-medium'>
          Notice of Admission
        </h3>
        {noaChoosable && (
          <RadioButtonGroup
            aria-label='Who files the Notice of Admission'
            value={effectiveNoa}
            onValueChange={value => {
              if (value === 'kinnser_filed' || value === 'roger_sends') {
                setNoaChoice(value);
              }
            }}
            options={[
              { value: 'kinnser_filed', label: NOA_EXTERNAL_FILED },
              {
                value: 'roger_sends',
                label: noaRogerSends(noaDue),
              },
            ]}
          />
        )}
        {noa.evidence_text && (
          <p className='text-sm text-muted-foreground'>
            External EMR claims tab: {noa.evidence_text}
          </p>
        )}
        {noa.action_required && (
          <p className='text-sm font-medium' data-testid='takeover-noa-action'>
            {noaFiledExternally ? NOA_ACTION_LINE : noaOverdueLine(noaDue)}
          </p>
        )}
        {noaPayerMismatch && (
          <div className='flex items-start gap-3'>
            <Checkbox
              id='takeover-noa-payer-confirmed'
              checkboxSize='default'
              checked={noaPayerConfirmed}
              onCheckedChange={checked =>
                setNoaPayerConfirmed(checked === true)
              }
            />
            <label htmlFor='takeover-noa-payer-confirmed' className='text-sm'>
              {noaPayerConfirmLabel(
                noaPayerMismatch.current,
                noaPayerMismatch.recorded
              )}
            </label>
          </div>
        )}
        {showNoaWarning && (
          <Alert data-testid='takeover-noa-warning'>
            <AlertDescription>{NOA_WARNING_LINE}</AlertDescription>
          </Alert>
        )}
      </section>
    );
  };

  const renderPlanStep = () => {
    if (!plan) return null;
    const noaLine =
      noa && effectiveNoa
        ? ` ${noaSentence(noaPhrase(noa, effectiveNoa))}`
        : '';
    return (
      <div className='space-y-4'>
        <p className='text-sm' data-testid='takeover-plan-summary'>
          {rogerBillsFromSentence(
            rogerBillsFrom ? displayDate(rogerBillsFrom) : null
          )}{' '}
          {rogerChartsFromSentence(displayDate(chartsFrom))}{' '}
          {externalKeepsSentence(kept.map(periodLabel))}
          {noaLine}
        </p>
        <p
          className='text-sm text-muted-foreground'
          data-testid='takeover-proposal-line'
        >
          {plan.proposal_line}
        </p>
        <div className='space-y-1' data-testid='takeover-charts-from'>
          <Label htmlFor='takeover-charts-from'>{CHARTS_FROM_LABEL}</Label>
          {chartsFromLocked ? (
            <p
              id='takeover-charts-from'
              className='text-sm font-medium'
              data-testid='takeover-charts-from-locked'
            >
              {displayDate(chartsFrom)}
            </p>
          ) : (
            <DatePicker
              id='takeover-charts-from'
              className='w-full md:w-48'
              datePickerSize='sm'
              allowClear={false}
              date={parseISOToLocalDate(chartsFrom) ?? undefined}
              minDate={
                parseISOToLocalDate(plan.roger_charts_from_earliest) ??
                undefined
              }
              maxDate={
                parseISOToLocalDate(plan.roger_charts_from_latest) ?? undefined
              }
              onDateChange={date => {
                if (!date) return;
                const day = formatDateToISO(date);
                pickedChartsFrom.current = day;
                setChartsFromReset(null);
                setChartsFrom(day);
              }}
            />
          )}
          <p
            className='text-sm text-muted-foreground'
            data-testid='takeover-charts-from-line'
          >
            {plan.roger_charts_from_line}
          </p>
          {chartsFromReset && (
            <p className='text-sm' data-testid='takeover-charts-from-reset'>
              {chartsFromResetLine(displayDate(chartsFromReset))}
            </p>
          )}
          {chartsFromEarlier && (
            <p className='text-sm' data-testid='takeover-charts-from-earlier'>
              {CHARTS_FROM_EARLIER_LINE}
            </p>
          )}
        </div>
        {usingOverride && (
          <p className='text-sm text-muted-foreground'>
            {frozenReasonLine(overrideReason.trim())}
          </p>
        )}
        {usingOverride && plan.history_arriving && (
          <Alert data-testid='takeover-plan-history'>
            <AlertDescription>{HISTORY_MISSING_OVERRIDE_LINE}</AlertDescription>
          </Alert>
        )}
        <ul className='space-y-3' aria-label='Claim periods'>
          {periods.map(period => {
            const custody = custodyOf(period);
            return (
              <li
                key={period.start_date}
                className='space-y-2 rounded-md border p-3'
                data-testid={`takeover-period-${period.number}`}
              >
                <p className='text-sm font-medium'>
                  Claim {period.number}: {displayDate(period.start_date)} –{' '}
                  {displayDate(period.end_date)}
                </p>
                {period.external_emr_claim && (
                  <p
                    className='text-sm text-muted-foreground'
                    data-testid={`takeover-period-claim-${period.number}`}
                  >
                    {EXTERNAL_CLAIM_READING_LINE[period.external_emr_claim]}
                  </p>
                )}
                <p
                  className='text-sm'
                  data-testid={`takeover-custody-${period.number}`}
                >
                  {custody === 'external_emr' ? 'External EMR' : 'Roger'}
                </p>
                {custody === 'external_emr' &&
                  period.unfinished_visits.map(visit => (
                    <p
                      key={visit.visit_id}
                      className='text-sm'
                      data-testid='takeover-unfinished-visit'
                    >
                      {unfinishedVisitLine(
                        visit.visit_date
                          ? `${visit.visit_type} on ${displayDate(visit.visit_date)}`
                          : visit.visit_type
                      )}
                    </p>
                  ))}
              </li>
            );
          })}
        </ul>
        {(plan.external_claim_lines ?? []).length > 0 && (
          <Alert data-testid='takeover-claim-lines'>
            <AlertDescription>
              <p className='font-medium'>{EXTERNAL_CLAIM_LINES_TITLE}</p>
              <ul className='mt-1 space-y-1'>
                {(plan.external_claim_lines ?? []).map((line, index) => (
                  <li key={index} className='break-words'>
                    {line}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
        {plan.external_claim_rows.length > 0 && (
          <section className='space-y-1'>
            <h3 className='text-sm font-medium'>External EMR claims tab</h3>
            <ul className='space-y-1 text-sm text-muted-foreground'>
              {plan.external_claim_rows.map((row, index) => (
                <li key={index} className='break-words'>
                  {row}
                </li>
              ))}
            </ul>
          </section>
        )}
        {renderNoa()}
        {plan.edited_row_count > 0 && (
          <p className='text-sm' data-testid='takeover-edited-rows'>
            {editedRowsLine(plan.edited_row_count)}
          </p>
        )}
      </div>
    );
  };

  // At a frozen agency Roger reads nothing more, so the confirm promises no
  // read and no sync review.
  const syncing = plan?.refresh.can_refresh ?? true;

  const renderConfirmStep = () => (
    <div className='space-y-4'>
      <p className='text-sm font-medium' data-testid='takeover-confirm-line'>
        {confirmLine(displayDate(chartsFrom), syncing)}
      </p>
      {chartsFromLater && (
        <p className='text-sm' data-testid='takeover-until-charts-from'>
          {untilChartsFromLine(displayDate(chartsFrom))}
        </p>
      )}
      <section
        className='space-y-2 rounded-md border bg-muted/40 p-3'
        aria-labelledby='takeover-checklist-heading'
        data-testid='takeover-checklist'
      >
        <h3 id='takeover-checklist-heading' className='text-sm font-medium'>
          {CHECKLIST_HEADING}
        </h3>
        <ul className='list-disc space-y-1 pl-5 text-sm'>
          <li>{CHECKLIST_REMAINING_VISITS}</li>
          {rogerPeriods.length > 0 && (
            <li>{checklistDoNotSend(rogerPeriods.map(p => p.number))}</li>
          )}
          {kept.length > 0 && (
            <li>
              {syncing ? CHECKLIST_SYNC_REVIEW : CHECKLIST_SYNC_REVIEW_FROZEN}
            </li>
          )}
        </ul>
      </section>
      {submitError && (
        <p className='text-sm text-destructive' role='alert'>
          {submitError}
        </p>
      )}
      {loadError && (
        <p
          className='text-sm text-destructive'
          role='alert'
          data-testid='takeover-reload-error'
        >
          {loadError}
        </p>
      )}
    </div>
  );

  const renderFooter = () => {
    if (step === 'blocked') {
      return (
        <>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
          {(phase === 'historyArriving' || loadError) && (
            <Button onClick={retryRefresh}>Try again</Button>
          )}
          {phase === 'unreadable' && plan?.can_override_access_lost && (
            <Button
              onClick={continueWithoutRefresh}
              disabled={!overrideReason.trim()}
            >
              {OVERRIDE_CONTINUE_LABEL}
            </Button>
          )}
        </>
      );
    }
    if (step === 'plan') {
      return (
        <>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setSubmitError(null);
              setStep('confirm');
            }}
            disabled={!chartsFromValid}
          >
            Next
          </Button>
        </>
      );
    }
    return (
      <>
        <Button
          variant='outline'
          onClick={() => setStep('plan')}
          disabled={submitting || planStale}
        >
          Back
        </Button>
        {planStale && !submitting && (
          <Button
            variant='outline'
            onClick={() => void reloadAfterConflict(generation.current)}
            disabled={reloading}
          >
            Try again
          </Button>
        )}
        <Button
          onClick={() => void handleConfirm()}
          disabled={submitting || planStale}
        >
          {submitting ? 'Taking over…' : 'Take over'}
        </Button>
      </>
    );
  };

  const stepLabel =
    step === 'plan' ? 'Step 1 of 2' : step === 'confirm' ? 'Step 2 of 2' : null;

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => !isOpen && !submitting && onClose()}
    >
      <DialogContent
        className='max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-xl'
        data-testid='take-over-dialog'
      >
        <DialogHeader>
          <DialogTitle>{TAKE_OVER_TITLE}</DialogTitle>
          {stepLabel && <DialogDescription>{stepLabel}</DialogDescription>}
        </DialogHeader>
        {notice && step !== 'confirm' && (
          <Alert data-testid='takeover-notice'>
            <AlertDescription>{notice}</AlertDescription>
          </Alert>
        )}
        {step === 'blocked' && renderRefreshStep()}
        {step === 'plan' && renderPlanStep()}
        {step === 'confirm' && renderConfirmStep()}
        <DialogFooter className='flex-col gap-2 sm:flex-row'>
          {renderFooter()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
